import { asUint8, decodeConfig, } from './config.js';
import { decodeUint32LE, decodeUtf8 } from './decode.js';
import { bytesToHex, describeError, emit, nowMs, } from './debug.js';
import { SoluuError, toSoluuError } from './errors.js';
import { decodeWiFiStatusFromDataView, } from './status.js';
const MAX_UTF8_BYTES = 50;
// §217 endpoint is a URL — bigger cap than SSID/password. Mobile app raises
// MTU to 512 on Android, so the firmware comfortably accepts longer payloads
// here than the 50-byte WiFi credential cap.
const MAX_PING_ENDPOINT_BYTES = 128;
// §313: WiFi Security is always written as 0xFF (firmware auto-detect)
// and is never a user-facing parameter.
const WIFI_SECURITY_AUTODETECT = 0xff;
// Char 314 takes any unrecognised byte to trigger a connect; convention is 0x01.
const WIFI_CONNECT_TRIGGER = 0x01;
// Char 314's deprovision opcode — firmware routes 0xE1 to
// wifi_delete_provision() (W-009). Same handle as the connect trigger.
const WIFI_DEPROVISION = 0xe1;
export class SoluuDeviceImpl {
    name;
    mac;
    firmwareVersion;
    radioFirmwareVersion;
    manufacturer;
    model;
    serialNumber;
    softwareRevision;
    canReadSSID;
    canReadPassword;
    hasWiFiError;
    missingExpected;
    device;
    chars;
    statusHandlers = new Set();
    wifiErrorHandlers = new Set();
    disconnectHandlers = new Set();
    statusListener;
    wifiErrorListener;
    disconnectListener;
    debug;
    connectedFlag = true;
    constructor(device, chars, identity, missingExpected, debug) {
        this.device = device;
        this.chars = chars;
        this.name = identity.name;
        this.mac = identity.mac;
        this.firmwareVersion = identity.firmwareVersion;
        this.radioFirmwareVersion = identity.radioFirmwareVersion;
        this.manufacturer = identity.manufacturer;
        this.model = identity.model;
        this.serialNumber = identity.serialNumber;
        this.softwareRevision = identity.softwareRevision;
        this.missingExpected = missingExpected;
        // Detect read-back support purely from the GATT `read` property, resolved
        // once at connect time. Shipping firmware exposes these write-only; a
        // debug build flips them readable. Never gate on firmware version — the
        // debug build's version string is unknown.
        this.canReadSSID = chars.wifiSsid.properties.read;
        this.canReadPassword = chars.wifiPassword.properties.read;
        // Capability = presence of the characteristic, never a version compare.
        this.hasWiFiError = chars.wifiError !== null;
        this.debug = debug;
        this.statusListener = (event) => {
            const target = event.target;
            const value = target?.value;
            if (!value)
                return;
            let status = null;
            let decodeError = null;
            try {
                status = decodeWiFiStatusFromDataView(value);
            }
            catch (cause) {
                decodeError = describeError(cause);
            }
            // Emit raw bytes BEFORE acting on them so a tester can see every
            // notification arrive even if decode would have thrown.
            emit(this.debug, {
                type: 'notify:value',
                charUuid: this.chars.wifiStatus.uuid,
                rawHex: bytesToHex(value),
                byteLength: value.byteLength,
                label: status?.label ?? null,
                decodeError,
                atMs: nowMs(),
            });
            if (!status)
                return;
            for (const handler of this.statusHandlers) {
                try {
                    handler(status);
                }
                catch {
                    /* handler errors are not the library's concern */
                }
            }
        };
        this.wifiErrorListener = (event) => {
            const target = event.target;
            const value = target?.value;
            if (!value)
                return;
            const wifiError = this.emitWiFiErrorValue(value);
            if (!wifiError)
                return;
            for (const handler of this.wifiErrorHandlers) {
                try {
                    handler(wifiError);
                }
                catch {
                    /* handler errors are not the library's concern */
                }
            }
        };
        this.disconnectListener = () => {
            this.connectedFlag = false;
            emit(this.debug, {
                type: 'gatt:disconnect',
                deviceId: this.device.id,
                deviceName: this.device.name ?? '',
                atMs: nowMs(),
            });
            for (const handler of this.disconnectHandlers) {
                try {
                    handler();
                }
                catch {
                    /* swallow */
                }
            }
        };
        this.chars.wifiStatus.addEventListener('characteristicvaluechanged', this.statusListener);
        // §315 listener attached here for the same reason as §216: addEventListener
        // does not replay, so attaching after startNotifications() would drop any
        // notification that fires in the gap. request.ts subscribes only after
        // this constructor returns.
        this.chars.wifiError?.addEventListener('characteristicvaluechanged', this.wifiErrorListener);
        // gattserverdisconnected is the source of truth for disconnection.
        this.device.addEventListener('gattserverdisconnected', this.disconnectListener);
    }
    get isConnected() {
        return this.connectedFlag && (this.device.gatt?.connected ?? false);
    }
    async setSSID(ssid) {
        const bytes = encodeUtf8Bounded(ssid, MAX_UTF8_BYTES, 'SSID');
        await this.write(this.chars.wifiSsid, bytes, 'ssid');
    }
    async setPassword(password) {
        const bytes = encodeUtf8Bounded(password, MAX_UTF8_BYTES, 'Password');
        await this.write(this.chars.wifiPassword, bytes, 'password');
    }
    async setPingEndpoint(endpoint) {
        const stripped = stripUrlScheme(endpoint);
        const bytes = encodeUtf8Bounded(stripped, MAX_PING_ENDPOINT_BYTES, 'Ping endpoint');
        await this.write(this.chars.wifiPingEndpoint, bytes, 'pingEndpoint');
    }
    async triggerConnect() {
        // Re-assert security mode every connect attempt so the user-facing API
        // stays a clean three-method sequence (setSSID/setPassword/triggerConnect)
        // without leaking the 0xFF requirement.
        await this.write(this.chars.wifiSecurity, new Uint8Array([WIFI_SECURITY_AUTODETECT]), 'security');
        await this.write(this.chars.wifiConnect, new Uint8Array([WIFI_CONNECT_TRIGGER]), 'connect');
    }
    async configureAndConnect(ssid, password, options) {
        await this.setSSID(ssid);
        await this.setPassword(password);
        if (options?.pingEndpoint) {
            await this.setPingEndpoint(options.pingEndpoint);
        }
        await this.triggerConnect();
    }
    async readWiFiStatus() {
        this.assertConnected();
        try {
            const value = await this.chars.wifiStatus.readValue();
            return decodeWiFiStatusFromDataView(value);
        }
        catch (cause) {
            throw toSoluuError(cause);
        }
    }
    async readStoredCredentials() {
        this.assertConnected();
        // Read sequentially, not via Promise.all: a Promise.all reject path is
        // what force-disconnected GATT in session 0006. Each read is independently
        // trapped below, so a failing one can never take down a succeeding one.
        const ssid = await this.readOneCredential(this.chars.wifiSsid, this.canReadSSID);
        const password = await this.readOneCredential(this.chars.wifiPassword, this.canReadPassword);
        return { ssid, password };
    }
    // No debug event is emitted for credential reads — matching readWiFiStatus,
    // and deliberately keeping the password value out of the debug channel.
    async readOneCredential(char, readable) {
        if (!readable) {
            return { readable: false, value: null, readFailed: false };
        }
        try {
            return { readable: true, value: decodeUtf8(await char.readValue()), readFailed: false };
        }
        catch {
            // Fully trapped: a failed read must not throw, disconnect, or surface a
            // raw exception. Treat it as unsupported; `readFailed` lets the caller
            // flag a characteristic that advertised `read` but rejected the read.
            return { readable: true, value: null, readFailed: true };
        }
    }
    onWiFiStatusChange(handler) {
        this.statusHandlers.add(handler);
        return () => {
            this.statusHandlers.delete(handler);
        };
    }
    async readBattery() {
        // null means "this device has no battery service", which is a capability
        // answer. A read that fails on a device that *does* have it is a real
        // failure and is surfaced, not swallowed.
        if (!this.chars.batteryLevel)
            return null;
        this.assertConnected();
        try {
            const view = await this.chars.batteryLevel.readValue();
            if (view.byteLength === 0)
                return null;
            return view.getUint8(0);
        }
        catch (cause) {
            throw toSoluuError(cause);
        }
    }
    async readConfiguration() {
        this.assertConnected();
        // Sequential and per-field trapped, same shape as readStoredCredentials.
        // Note D-003: firmware's config write branches never call
        // rsi_ble_set_local_att_value, so a read-back within one connection may
        // return the value captured at profile init rather than what was written.
        // Surfacing that faithfully is the point — do not compensate for it here.
        const pulseIntensity = await this.readViewOrNull(this.chars.pulseIntensity);
        const volumeEnabled = await this.readViewOrNull(this.chars.volumeEnabled);
        const volumeLevel = await this.readViewOrNull(this.chars.volumeLevel);
        const vibrationEnabled = await this.readViewOrNull(this.chars.vibrationEnabled);
        return decodeConfig({
            pulseIntensity,
            volumeEnabled,
            volumeLevel,
            vibrationEnabled,
        });
    }
    async readViewOrNull(char) {
        if (!char)
            return null;
        try {
            const view = await char.readValue();
            return view.byteLength > 0 ? view : null;
        }
        catch {
            // One failing field must not take down the other three.
            return null;
        }
    }
    async setPulseIntensity(value) {
        await this.writeSetting(this.chars.pulseIntensity, 'Pulse Intensity', '211', asUint8(value, 'Pulse Intensity'), 'pulseIntensity');
    }
    async setVolumeLevel(value) {
        await this.writeSetting(this.chars.volumeLevel, 'Volume Level', '213', asUint8(value, 'Volume Level'), 'volumeLevel');
    }
    async setVolumeEnabled(enabled) {
        await this.writeSetting(this.chars.volumeEnabled, 'Volume Enabled', '212', enabled ? 1 : 0, 'volumeEnabled');
    }
    async setVibrationEnabled(enabled) {
        await this.writeSetting(this.chars.vibrationEnabled, 'Vibration Enabled', '214', enabled ? 1 : 0, 'vibrationEnabled');
    }
    // asUint8 is the ONLY validation on this path — a structural bound, because
    // the characteristic is a uint8. Values outside the nominal range are sent
    // verbatim: the tester must be able to write intensity 123 and observe what
    // the firmware does with it (D-006). Never clamp here.
    async writeSetting(char, name, uuidSuffix, byte, label) {
        if (!char) {
            throw new SoluuError('GATT_ERROR', `${name} (${uuidSuffix}) was not found on this device`);
        }
        await this.write(char, new Uint8Array([byte]), label);
    }
    async deprovision() {
        await this.write(this.chars.wifiConnect, new Uint8Array([WIFI_DEPROVISION]), 'deprovision');
    }
    async readWiFiError() {
        if (!this.chars.wifiError)
            return null;
        this.assertConnected();
        try {
            const view = await this.chars.wifiError.readValue();
            return this.emitWiFiErrorValue(view);
        }
        catch (cause) {
            // Trapped, but never silent: §315 is diagnostic surface, so a failed
            // read is recorded in the same row a tester is already watching rather
            // than thrown into a caller that has nothing useful to do with it.
            emit(this.debug, {
                type: 'wifiError:value',
                charUuid: this.chars.wifiError.uuid,
                rawHex: '',
                byteLength: 0,
                value: null,
                valueHex: null,
                decodeError: describeError(cause),
                atMs: nowMs(),
            });
            return null;
        }
    }
    onWiFiErrorChange(handler) {
        // No-op subscription when §315 is absent — the caller doesn't branch.
        if (!this.chars.wifiError)
            return () => { };
        this.wifiErrorHandlers.add(handler);
        return () => {
            this.wifiErrorHandlers.delete(handler);
        };
    }
    // Shared by the notify path and the on-demand read: emit raw bytes first so
    // an arrival is visible even when decode would throw, then return the
    // decoded value (or null if it didn't decode).
    emitWiFiErrorValue(view) {
        let wifiError = null;
        let decodeError = null;
        try {
            const raw = decodeUint32LE(view);
            wifiError = { raw, hex: formatWiFiErrorHex(raw), timestamp: Date.now() };
        }
        catch (cause) {
            decodeError = describeError(cause);
        }
        emit(this.debug, {
            type: 'wifiError:value',
            charUuid: this.chars.wifiError?.uuid ?? '',
            rawHex: bytesToHex(view),
            byteLength: view.byteLength,
            value: wifiError?.raw ?? null,
            valueHex: wifiError?.hex ?? null,
            decodeError,
            atMs: nowMs(),
        });
        return wifiError;
    }
    async disconnect() {
        // gatt.disconnect() is fire-and-forget; trust the
        // gattserverdisconnected event to flip our connected flag.
        try {
            this.device.gatt?.disconnect();
        }
        catch {
            /* ignore */
        }
    }
    onDisconnect(handler) {
        this.disconnectHandlers.add(handler);
        return () => {
            this.disconnectHandlers.delete(handler);
        };
    }
    async write(char, value, label) {
        this.assertConnected();
        const valueHex = hexForWrite(label, value);
        const valuePreview = previewForWrite(label, value);
        emit(this.debug, {
            type: 'write:start',
            charUuid: char.uuid,
            label,
            byteLength: value.byteLength,
            valueHex,
            valuePreview,
            atMs: nowMs(),
        });
        try {
            await char.writeValueWithResponse(value);
            emit(this.debug, {
                type: 'write:ok',
                charUuid: char.uuid,
                label,
                byteLength: value.byteLength,
                valueHex,
                valuePreview,
                atMs: nowMs(),
            });
        }
        catch (cause) {
            emit(this.debug, {
                type: 'write:error',
                charUuid: char.uuid,
                label,
                byteLength: value.byteLength,
                valueHex,
                valuePreview,
                error: describeError(cause),
                atMs: nowMs(),
            });
            throw toSoluuError(cause);
        }
    }
    assertConnected() {
        if (!this.isConnected) {
            throw new SoluuError('NOT_CONNECTED', 'Device is not connected');
        }
    }
}
// Uppercase, zero-padded to 8 — deliberately matching the firmware's own
// `LOG_PRINT(... 0x%08lX ...)` rather than this codebase's lowercase hex
// convention, because the entire point of carrying the hex form is comparing
// it against a serial capture by eye.
function formatWiFiErrorHex(raw) {
    return `0x${raw.toString(16).toUpperCase().padStart(8, '0')}`;
}
// Password is intentionally redacted from BOTH the hex and the preview:
// the SPEC §"Logs" example records `passwordLength` rather than the
// cleartext, and diagnostics are routinely copied/exported by testers.
// Until 2026-05 we redacted `valuePreview` but kept `valueHex`, which
// decoded byte-for-byte to the cleartext — defeating the whole point.
// Now both are null for password writes; `byteLength` survives so a
// debugger can still see how long the password was.
function hexForWrite(label, value) {
    if (label === 'password')
        return null;
    return bytesToHex(value);
}
function previewForWrite(label, value) {
    switch (label) {
        case 'ssid':
        case 'pingEndpoint':
            try {
                return new TextDecoder('utf-8', { fatal: false }).decode(value);
            }
            catch {
                return null;
            }
        case 'security':
        case 'connect':
        case 'pulseIntensity':
        case 'volumeEnabled':
        case 'volumeLevel':
        case 'vibrationEnabled':
        case 'deprovision':
            return value.byteLength === 1
                ? `0x${value[0].toString(16).padStart(2, '0')}`
                : null;
        case 'password':
            return null;
    }
}
function encodeUtf8Bounded(value, maxBytes, label) {
    const bytes = new TextEncoder().encode(value);
    if (bytes.length > maxBytes) {
        throw new SoluuError('GATT_ERROR', `${label} exceeds ${maxBytes} byte limit (got ${bytes.length})`);
    }
    // Copy into a fresh ArrayBuffer so the buffer type is concretely
    // `ArrayBuffer` (not `ArrayBufferLike`), matching what
    // writeValueWithResponse expects under TS 5.9 lib.dom.
    const out = new Uint8Array(bytes.byteLength);
    out.set(bytes);
    return out;
}
export function stripUrlScheme(url) {
    // Mobile app strips the `https://` prefix before writing — firmware adds
    // its own scheme. Match that contract; trim whitespace too.
    const trimmed = url.trim();
    if (trimmed.startsWith('https://'))
        return trimmed.slice('https://'.length);
    if (trimmed.startsWith('http://'))
        return trimmed.slice('http://'.length);
    return trimmed;
}
//# sourceMappingURL=device.js.map